import React from 'react';
import Form, {
  useFormContext,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import DataEntryModal from '@grnhse/seedling/lib/birch/components/modals/data_entry_modal';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import { withLocalePrefix } from 'shared/utils/translation';
import styled from 'styled-components';
import CancelButton from '@grnhse/seedling/lib/birch/components/forms/cancel_button';
import TextField from '@grnhse/seedling/lib/birch/components/forms/fields/text_field';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { NestedFormSubmitButton } from './shared';
import { FieldValues } from 'react-hook-form/dist/types/fields';

const t = withLocalePrefix(
  'generic_booking.tinymce_editor.insert_web_link_modal'
);

interface InsertWebLinkProps {
  showModal: boolean;
  handleClose: () => void;
  insertLink: (href: string, text?: string) => void;
  initialData?: { href: string; text: string } | null;
  isEditing?: boolean;
}

interface InsertWebLinkForm {
  url: string;
  displayText: string;
}

export const ModalBodyWrapper = styled.div`
  padding-top: 4px;
  padding-bottom: 20px;
`;

const Modal = ({
  handleClose,
  insertLink,
  initialData,
  isEditing,
}: Omit<InsertWebLinkProps, 'showModal'>) => {
  const { reset } = useFormContext();

  const fetchDefault = React.useCallback(() => {
    if (!initialData) {
      return { url: '', displayText: '' };
    }

    if (isEditing) {
      return {
        url: initialData.href,
        displayText:
          initialData.text !== initialData.href ? initialData.text : '',
      };
    } else if (initialData.text && !initialData.href) {
      // When inserting new link with selected text
      return {
        url: '',
        displayText: initialData.text,
      };
    }

    return { url: '', displayText: '' };
  }, [initialData, isEditing]);

  React.useEffect(() => {
    reset(fetchDefault());
  }, [fetchDefault, reset]);

  const resetAndCloseModal = () => {
    reset();
    handleClose();
  };

  const saveChanges = async (data: FieldValues) => {
    insertLink(data.url, data.displayText || data.url);
    resetAndCloseModal();
  };

  return (
    <>
      <DataEntryModal.Header onClick={resetAndCloseModal}>
        {isEditing ? t('title.edit') : t('title.insert')}
      </DataEntryModal.Header>
      <DataEntryModal.Body>
        <ModalBodyWrapper data-provides={'insert-web-link-modal'}>
          <Stack spacing={3}>
            <TextField name={'url'} label={t('url')} />
            <TextField name={'displayText'} label={t('text_to_display')} />
          </Stack>
        </ModalBodyWrapper>
      </DataEntryModal.Body>
      <DataEntryModal.Footer>
        <Grid
          container
          spacing={2}
          alignItems="flex-start"
          direction="row"
          justifyContent="flex-end"
          flexWrap="wrap-reverse"
          sx={{
            padding: '8px',
          }}
        >
          <Grid>
            <CancelButton onClick={resetAndCloseModal}>
              {t('cancel')}
            </CancelButton>
          </Grid>
          <Grid>
            <NestedFormSubmitButton
              data-provides={'submit-insert-web-link'}
              buttonText={isEditing ? t('update') : t('add')}
              handleSubmit={saveChanges}
            />
          </Grid>
        </Grid>
      </DataEntryModal.Footer>
    </>
  );
};

const InsertWebLinkModal = ({
  showModal,
  handleClose,
  insertLink,
  initialData,
  isEditing,
}: InsertWebLinkProps) => {
  return (
    <DataEntryModal
      size={'medium'}
      isOpen={showModal}
      onDismiss={handleClose}
      onSubmit={() => null}
      ariaLabel={'insert-web-link-modal'}
    >
      <Form<InsertWebLinkForm>
        onSubmit={() => {}} // eslint-disable-line @typescript-eslint/no-empty-function
        mode={'onBlur'}
      >
        <Modal
          handleClose={handleClose}
          insertLink={insertLink}
          initialData={initialData}
          isEditing={isEditing}
        />
      </Form>
    </DataEntryModal>
  );
};

export default InsertWebLinkModal;
